import { safeParse } from "./safeParse";

const STORAGE_KEY = "hb_ai_gameplay_exposure";

interface ExposureRecord {
  id: string;
  count: number;
  lastExposureTime: number;
}

interface ExposureStorage {
  [id: string]: ExposureRecord;
}

/**
 * 获取所有AI玩法的曝光记录
 */
export function getAiGameplayExposureRecords(): ExposureStorage {
  if (typeof window === "undefined") {
    return {};
  }
  return safeParse(localStorage.getItem(STORAGE_KEY)) || {};
}

/**
 * 获取指定AI玩法的曝光次数
 */
export function getAiGameplayExposureCount(id: string | number): number {
  const records = getAiGameplayExposureRecords();
  const key = String(id);
  return records[key]?.count || 0;
}

/**
 * 增加AI玩法的曝光次数
 */
export function incrementAiGameplayExposure(id: string | number): void {
  if (typeof window === "undefined") {
    return;
  }
  const records = getAiGameplayExposureRecords();
  const key = String(id);
  const existingRecord = records[key];

  records[key] = {
    id: key,
    count: (existingRecord?.count || 0) + 1,
    lastExposureTime: Date.now(),
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

/**
 * 检查AI玩法是否已达到最大曝光次数（3次）
 */
export function isAiGameplayMaxExposed(id: string | number): boolean {
  return getAiGameplayExposureCount(id) >= 3;
}

/**
 * 过滤并排序AI玩法列表
 * 1. 过滤掉已达到3次曝光的
 * 2. 按曝光次数排序（优先展示曝光次数少的）
 * 3. 曝光次数相同时，保持原始配置顺序
 */
export function filterAndSortAiGameplayList<T extends { id: string | number }>(
  list: T[]
): T[] {
  // 记录原始索引，用于稳定排序
  const listWithIndex = list.map((item, index) => ({
    item,
    originalIndex: index,
    exposureCount: getAiGameplayExposureCount(item.id),
  }));

  return (
    listWithIndex
      // 过滤：移除已达到3次曝光的
      .filter(({ exposureCount }) => exposureCount < 3)
      // 排序：优先展示曝光次数少的，相同曝光次数按原始顺序
      .sort((a, b) => {
        if (a.exposureCount !== b.exposureCount) {
          return a.exposureCount - b.exposureCount;
        }
        // 曝光次数相同，保持原始顺序
        return a.originalIndex - b.originalIndex;
      })
      .map(({ item }) => item)
  );
}
